import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  Pagination,
  Paper,
  Stack,
  Typography,
  Box,
  Card,
  Divider,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { alpha, typography } from "@mui/system";
import { styled } from "@mui/material/styles";
import TableItem from "../../../components/TableItem";
import {
  getImCategoryDataByFilter,
  getImportsByCategory,
} from "../../../services/Import";
import Im_CategoryForm from "./Im_CategoryForm";
import CircularProgress from "@mui/material/CircularProgress";
import { useHistory } from "react-router";
import InputField from "../../../FormComponents/InputField";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CategoryIcon from "@mui/icons-material/Category";
import { RoleBasedRender } from "../../../components/RoleBaseRender";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.light, 0.05),
  marginBottom: theme.spacing(3),
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

let _imCategoryListState = null;

function Im_Categories() {
  const history = useHistory();

  // State management
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Table columns configuration
  const columns = [
    { id: "category", label: "Category", minWidth: 120 },
    { id: "subCat_1", label: "Tag 1", minWidth: 100 },
    { id: "subCat_2", label: "Tag 2", minWidth: 100 },
    { id: "subCat_3", label: "Tag 3", minWidth: 100 },
    { id: "subCat_4", label: "Tag 4", minWidth: 100 },
    { id: "subCat_5", label: "Tag 5", minWidth: 100 },
    {
      id: "count",
      label: "Item Count",
      minWidth: 100,
      format: (value) => (
        <Chip
          label={value}
          color={value > 0 ? "primary" : "default"}
          size="small"
          variant="outlined"
        />
      ),
    },
    { id: "action", label: "Actions", minWidth: 120 },
  ];

  // Create data row factory function
  function createData(
    category,
    subCat_1,
    subCat_2,
    subCat_3,
    subCat_4,
    subCat_5,
    count,
    action,
    cat_id
  ) {
    return {
      category,
      subCat_1,
      subCat_2,
      subCat_3,
      subCat_4,
      subCat_5,
      count,
      action,
      cat_id,
    };
  }

  // Filter state
  const [filter, setFilter] = useState({
    _category: "",
    _subCat_1: "",
    _subCat_2: "",
    _subCat_3: "",
    _subCat_4: "",
    _subCat_5: "",
  });

  // Initial data load
  useEffect(() => {
    if (_imCategoryListState) {
      const { page: savedPage, filter: savedFilter } = _imCategoryListState;
      _imCategoryListState = null;
      setPage(savedPage);
      setFilter(savedFilter);
      fetchData(savedFilter, savedPage);
    } else {
      fetchData(filter, page);
    }
  }, []);

  // Pagination handler
  const handleChange = async (event, value) => {
    setPage(value - 1);
    await fetchData(filter, value - 1);
  };

  // Fetch data from API
  const fetchData = async (categoryFilter, pageNo) => {
    setLoading(true);
    try {
      // Get categories data based on filters
      let result = await getImCategoryDataByFilter(
        pageNo,
        rowsPerPage,
        categoryFilter
      );
      let categorySet = result.content;

      // Set total pages for pagination
      setTotalPages(result.totalPages);
      setTotalRows(result?.totalElements || categorySet?.length || 0);

      const newSet = [];
      if (categorySet && categorySet.length > 0) {
        for (let x = 0; x < categorySet.length; x++) {
          // Get imports count for this category
          let import_list = await getImportsByCategory(categorySet[x].cat_id);

          // Set data in new set list to display in the table
          newSet.push(
            createData(
              categorySet[x].category,
              categorySet[x].subCat_1,
              categorySet[x].subCat_2,
              categorySet[x].subCat_3,
              categorySet[x].subCat_4,
              categorySet[x].subCat_5,
              import_list?.length || 0,
              categorySet[x].cat_id
            )
          );
        }
      }

      // Set rows to table
      setRows(newSet);
    } catch (error) {
      console.error("Error fetching category data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Action handler for table actions
  const handleAction = (event, id) => {
    switch (event) {
      case "view":
        _imCategoryListState = { page, filter };
        history.push("/template/im_category_view/" + id);
        break;
      case "edit":
        history.push("/template/im_category_edit/" + id);
        break;
      case "delete":
        // Implement delete functionality here
        console.log("Delete category with ID:", id);
        break;
      default:
        return;
    }
  };

  // Reset filters
  const resetFilters = () => {
    setFilter({
      _category: "",
      _subCat_1: "",
      _subCat_2: "",
      _subCat_3: "",
      _subCat_4: "",
      _subCat_5: "",
    });
  };

  // Handle filter change
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <StyledPaper elevation={0}>
      {openForm ? (
        <Im_CategoryForm setOpenForm={setOpenForm} />
      ) : (
        <>
          <HeaderCard>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <CategoryIcon fontSize="large" />
              </Grid>
              <Grid item xs>
                <Typography variant="h5" fontWeight="bold">
                  Categories Management
                </Typography>
                <Typography variant="body2">
                  View and manage product categories and tags
                </Typography>
              </Grid>
              <Grid item>
                <RoleBasedRender
                  allowedRoles={["INVENTORY_CREATE", "ROLE_ADMIN"]}
                >
                  <ActionButton
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenForm(true)}
                  >
                    Add Category
                  </ActionButton>
                </RoleBasedRender>
              </Grid>
            </Grid>
          </HeaderCard>

          <Box
            mb={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <TextField
              placeholder="Search by category name..."
              variant="outlined"
              size="small"
              sx={{ width: "40%" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => {
                handleFilterChange("_category", e.target.value);
                if (e.target.value.length > 2 || e.target.value.length === 0) {
                  fetchData({ ...filter, _category: e.target.value }, page);
                }
              }}
            />
            <Stack direction="row" spacing={2}>
              <Tooltip title="Toggle Filters">
                <ActionButton
                  variant="outlined"
                  color="primary"
                  startIcon={<FilterListIcon />}
                  onClick={toggleFilters}
                >
                  Filters
                </ActionButton>
              </Tooltip>
              <Chip
                label={`${totalRows} Categories`}
                variant="outlined"
                color="primary"
              />
            </Stack>
          </Box>

          {showFilters && (
            <FilterContainer>
              <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                Advanced Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <InputField
                    name="_category"
                    value={filter._category}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Category"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputField
                    name="_subCat_1"
                    value={filter._subCat_1}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Tag 1"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputField
                    name="_subCat_2"
                    value={filter._subCat_2}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Tag 2"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputField
                    name="_subCat_3"
                    value={filter._subCat_3}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Tag 3"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputField
                    name="_subCat_4"
                    value={filter._subCat_4}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Tag 4"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputField
                    name="_subCat_5"
                    value={filter._subCat_5}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Tag 5"
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Stack direction="row" spacing={2}>
                  <Tooltip title="Reset Filters">
                    <ActionButton
                      variant="outlined"
                      color="secondary"
                      startIcon={<RestartAltIcon />}
                      onClick={() => {
                        resetFilters();
                        fetchData({}, page);
                      }}
                    >
                      Reset
                    </ActionButton>
                  </Tooltip>
                  <Tooltip title="Apply Filters">
                    <ActionButton
                      variant="contained"
                      color="primary"
                      onClick={() => fetchData(filter, page)}
                    >
                      Apply Filters
                    </ActionButton>
                  </Tooltip>
                </Stack>
              </Box>
            </FilterContainer>
          )}

          <Divider sx={{ my: 2 }} />

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ textAlign: "center", p: 5 }}>
              <Typography variant="body1" color="textSecondary">
                No categories found. Try adjusting your filters or add a new
                category.
              </Typography>
            </Box>
          ) : (
            <TableItem
              columns={columns}
              rows={rows}
              page={page}
              setPage={setPage}
              tablePagin={false}
              totalPages={totalPages}
              handleAction={handleAction}
              showActions={{
                view: "Category view",
                edit: "Category edit",
                disable: "Category Diactvate",
              }}
              actionRoles={{
                view: ["INVENTORY_VIEW", "ROLE_ADMIN"],
                edit: ["INVENTORY_UPDATE", "ROLE_ADMIN"],
                disable: ["INVENTORY_DELETE", "ROLE_ADMIN"],
              }}
              tableProps={{
                sx: {
                  "& .MuiTableRow-root:hover": {
                    backgroundColor: alpha("#000", 0.04),
                  },
                },
              }}
            />
          )}

          {totalPages > 1 && (
            <PaginationContainer>
              <Pagination
                count={totalPages}
                page={page + 1}
                variant="outlined"
                shape="rounded"
                color="primary"
                onChange={handleChange}
                size="medium"
              />
            </PaginationContainer>
          )}
        </>
      )}
    </StyledPaper>
  );
}

export default Im_Categories;
